'use client';

import Link from 'next/link';
import { ChevronRight, CreditCard, Gem, Loader2, Star } from 'lucide-react';
import { useLocalHref } from '../navigation/base-path';
import { cn } from '../utils/cn';

export type CreditsPaymentProvider = 'YOOKASSA' | 'TELEGRAM_STARS' | 'PLATEGA' | 'AURAPAY';

export interface CreditsScreenPackage {
  id: string;
  name: string | null;
  credits: number;
  priceRubles: number;
  priceStars: number;
}

export interface CreditsScreenProvider {
  id: string;
  name: string;
  description?: string | null;
}

interface CreditsScreenProps {
  balance: string;
  packages: CreditsScreenPackage[];
  providers: CreditsScreenProvider[];
  packagesLoading?: boolean;
  selectedPackage: CreditsScreenPackage | null;
  customAmount: string;
  customCredits: number;
  customPriceRubles: number;
  customPriceStars: number;
  paymentProvider: CreditsPaymentProvider | null;
  paymentPending?: boolean;
  hasSelection: boolean;
  displayPrice: string | null;
  onSelectPackage: (pkg: CreditsScreenPackage) => void;
  onCustomAmountChange: (value: string) => void;
  onCustomAmountFocus: () => void;
  onSelectProvider: (provider: CreditsPaymentProvider) => void;
  onPay: () => void;
  onHistoryClick?: () => void;
  onProviderSelection?: () => void;
  /** Optional back-button handler. Web mobile passes this so the user
   * can leave the balance page without the browser's chrome; the miniapp
   * leaves it undefined and relies on Telegram's native BackButton. */
  onBack?: () => void;
}

export function CreditsScreen({
  balance,
  packages,
  providers,
  packagesLoading = false,
  selectedPackage,
  customAmount,
  customCredits,
  customPriceRubles,
  customPriceStars,
  paymentProvider,
  paymentPending = false,
  hasSelection,
  displayPrice,
  onSelectPackage,
  onCustomAmountChange,
  onCustomAmountFocus,
  onSelectProvider,
  onPay,
  onHistoryClick,
  onProviderSelection,
  onBack,
}: CreditsScreenProps) {
  const isCustom = customAmount.length > 0 && !selectedPackage;
  const creditsHistoryHref = useLocalHref('/credits/history');

  return (
    <div className="min-h-screen bg-background">
      <div className="sticky top-0 z-10 flex items-center gap-3 bg-background px-4 pb-3">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            aria-label="Назад"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#242424]/40"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              stroke="#fafafa"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M11 4L5 10L11 16" />
              <line x1="5" y1="10" x2="16" y2="10" />
            </svg>
          </button>
        )}
        <h1 className="font-benzin text-base text-[#f2f2f7]">
          Баланс
        </h1>
      </div>

      <div className="flex flex-col gap-3 px-4 pt-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="font-benzin text-[40px] font-semibold leading-none text-primary">{formatCurrency(balance)}</span>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/assets/gem-icon.svg" alt="" className="mt-1 h-[26px] w-[26px]" />
          </div>
          <Link
            href={creditsHistoryHref}
            onClick={onHistoryClick}
            className="flex items-center gap-1"
          >
            <span className="font-benzin text-base font-semibold text-white">История</span>
            <ChevronRight className="h-4 w-4 text-white" />
          </Link>
        </div>

        <div
          className="overflow-hidden rounded-xl p-4"
          style={{
            background: 'linear-gradient(180deg, rgba(153,153,153,0.15) 0%, rgba(51,51,51,0.25) 100%)',
            backdropFilter: 'blur(40px)',
            WebkitBackdropFilter: 'blur(40px)',
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: 'inset 0 1px 0 0 rgba(255,255,255,0.1), inset 0 -1px 0 0 rgba(255,255,255,0.02)',
          }}
        >
          <p className="mb-3 font-benzin text-xs text-[#f2f2f7]">Выберите пакет</p>

          {packagesLoading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : packages.length > 0 ? (
            <div className="grid grid-cols-3 gap-3">
              {packages.map((pkg) => {
                const isSelected = selectedPackage?.id === pkg.id;
                return (
                  <button
                    key={pkg.id}
                    onClick={() => onSelectPackage(pkg)}
                    className={cn(
                      'relative flex flex-col items-center rounded-xl px-1 py-3.5 transition-all duration-200',
                      isSelected
                        ? 'bg-accent/20 ring-[1.5px] ring-accent shadow-[0_0_12px_rgba(157,217,74,0.15)]'
                        : 'bg-white/[0.04] ring-[1px] ring-white/[0.08]',
                    )}
                  >
                    {pkg.name && (
                      <span
                        className="absolute left-1/2 -top-2 -translate-x-1/2 rounded-full px-2 py-[1px] text-[9px] font-bold uppercase tracking-wide"
                        style={{ background: 'linear-gradient(135deg, #ffe066, #f5d500)', color: '#1a1a00' }}
                      >
                        {pkg.name}
                      </span>
                    )}
                    <div className="flex items-center gap-1">
                      <span className="text-[22px] font-bold leading-none text-white">{pkg.credits}</span>
                      <Gem className="h-4 w-4 text-cyan-400" />
                    </div>
                    <span className="mt-1 text-[11px] text-[#8e8e93]">кредитов</span>
                    <span className="mt-1.5 text-[13px] font-medium text-white/80">
                      {paymentProvider === 'TELEGRAM_STARS'
                        ? `${pkg.priceStars} ⭐`
                        : `${pkg.priceRubles} ₽`}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="py-4 text-center text-sm text-muted-foreground">
              Пакеты не настроены
            </p>
          )}
        </div>

        <div
          className="overflow-hidden rounded-xl p-4"
          style={{
            background: 'linear-gradient(180deg, rgba(153,153,153,0.15) 0%, rgba(51,51,51,0.25) 100%)',
            backdropFilter: 'blur(40px)',
            WebkitBackdropFilter: 'blur(40px)',
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: 'inset 0 1px 0 0 rgba(255,255,255,0.1), inset 0 -1px 0 0 rgba(255,255,255,0.02)',
          }}
        >
          <p className="mb-3 font-benzin text-xs text-[#f2f2f7]">Своя сумма</p>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder="Минимальная сумма 100 ₽"
            value={customAmount}
            onChange={(event) => onCustomAmountChange(event.target.value)}
            onFocus={onCustomAmountFocus}
            className="w-full rounded-lg bg-[#040404] px-3 py-2.5 text-base text-white placeholder:text-[#8e8e93] focus:outline-none focus:ring-1 focus:ring-accent/50"
          />
        </div>

        {providers.length > 0 && (
          <div
            className="overflow-hidden rounded-xl"
            style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            <div className="px-4 pb-1 pt-3">
              <p className="font-benzin text-xs text-[#f2f2f7]">Способ оплаты</p>
            </div>

            {providers.map((provider, idx) => {
              const isSelected = paymentProvider === provider.id;
              const isLast = idx === providers.length - 1;

              const subtitle = getProviderSubtitle({
                providerId: provider.id as CreditsPaymentProvider,
                providerDescription: provider.description || '',
                isCustom,
                customCredits,
                customPriceRubles,
                customPriceStars,
                selectedPackage,
              });

              return (
                <button
                  key={provider.id}
                  onClick={() => {
                    onProviderSelection?.();
                    onSelectProvider(provider.id as CreditsPaymentProvider);
                  }}
                  className="flex w-full items-center"
                >
                  <div className="flex w-10 items-center justify-center pl-2.5">
                    {provider.id === 'TELEGRAM_STARS' ? (
                      <Star className="h-6 w-6 fill-yellow-500 text-yellow-500" />
                    ) : (
                      <CreditCard className="h-6 w-6 text-white" />
                    )}
                  </div>

                  <div className={cn('flex flex-1 items-center justify-between py-3 pl-2.5 pr-4', !isLast && 'border-b border-[#4e4e50]/50')}>
                    <div className="flex flex-col items-start gap-0.5 text-left">
                      <span className="text-base font-semibold text-[#e7e8ea]" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                        {provider.name}
                      </span>
                      <span className="text-left text-xs text-[#e7e8ea]">
                        {subtitle}
                      </span>
                    </div>

                    <div
                      className={cn(
                        'flex h-7 w-7 items-center justify-center rounded-full transition-colors',
                        isSelected ? 'bg-accent' : 'border border-[#4e4e50] bg-[#25272a]',
                      )}
                    >
                      {isSelected && <div className="h-2.5 w-2.5 rounded-full bg-white" />}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="px-4 pb-6 pt-3">
        <button
          onClick={onPay}
          disabled={!hasSelection || paymentPending}
          className={cn(
            'flex w-full items-center justify-center gap-2 rounded-[20px] py-3.5 text-base font-semibold text-white transition-all',
            hasSelection
              ? 'bg-[#ef6444] shadow-[0_2px_5px_rgba(0,0,0,0.1),0_10px_9px_rgba(0,0,0,0.09),0_24px_13px_rgba(0,0,0,0.05)]'
              : 'bg-[#202020]/60 text-white/40',
          )}
        >
          {paymentPending ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Создание платежа...
            </>
          ) : hasSelection ? (
            <>Оплатить {displayPrice}</>
          ) : (
            'Выберите сумму'
          )}
        </button>
      </div>
    </div>
  );
}

function formatCurrency(amount: string | number): string {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  return num.toLocaleString('ru-RU');
}

function getProviderSubtitle({
  providerId,
  providerDescription,
  isCustom,
  customCredits,
  customPriceRubles,
  customPriceStars,
  selectedPackage,
}: {
  providerId: CreditsPaymentProvider;
  providerDescription: string;
  isCustom: boolean;
  customCredits: number;
  customPriceRubles: number;
  customPriceStars: number;
  selectedPackage: CreditsScreenPackage | null;
}) {
  if (providerId === 'TELEGRAM_STARS') {
    const price = isCustom && customCredits >= 10
      ? customPriceStars
      : selectedPackage?.priceStars;
    return price ? 'Оплатить балансом в телеграм' : providerDescription;
  }

  const price = isCustom && customCredits >= 10
    ? customPriceRubles
    : selectedPackage?.priceRubles;
  return price ? `Оплатить картой ${price} ₽` : providerDescription;
}
